import { useEffect, useState } from "react";
import { api, RAYG, RAYG_TEXT, SCORE_DIMS, EVIDENCE } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";

export default function Diagnostics() {
  const [diags, setDiags] = useState([]);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState(50);

  const load = () => Promise.all([api.get("/diagnostics"), api.get("/departments")]).then(([dg, d]) => { setDiags(dg.data); setDepts(d.data); });
  useEffect(() => { load(); }, []);

  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));

  const save = async () => {
    await api.put(`/diagnostics/${editing.dept}`, { dimension: editing.dim, value: Number(value) });
    toast.success("Score updated — recorded in audit trail as user-reported");
    setEditing(null); load();
  };

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="diagnostics-page">
      <PageHeader overline="Diagnostic & Scoring Model" title="Enterprise Heat Map"
        subtitle="Twelve dimensions scored 0–100 per department. Click any cell to adjust — edits are logged and marked as user-reported evidence." />
      <div className="px-8">
        <div className="bg-white border border-black/10 rounded-sm overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="diagnostics-table">
            <thead>
              <tr className="bg-[#0A0A0A] text-[#D4AF37]">
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-[0.12em] sticky left-0 bg-[#0A0A0A]">Department</th>
                {SCORE_DIMS.map(([k, l]) => <th key={k} className="px-1 py-3 text-[9px] uppercase tracking-wide font-medium min-w-[62px]">{l.split(" ").map((w, i) => <span key={i} className="block">{w}</span>)}</th>)}
              </tr>
            </thead>
            <tbody>
              {diags.map((dg) => (
                <tr key={dg.department_key} className="border-b border-black/5" data-testid={`diag-row-${dg.department_key}`}>
                  <td className="px-3 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2" style={{ background: deptByKey[dg.department_key]?.color }} />
                    {deptByKey[dg.department_key]?.name}
                  </td>
                  {SCORE_DIMS.map(([k]) => {
                    const s = dg.scores[k];
                    return (
                      <td key={k} className="p-1 text-center">
                        <button onClick={() => { setEditing({ dept: dg.department_key, dim: k, label: k }); setValue(s.value); }}
                          data-testid={`diag-cell-${dg.department_key}-${k}`}
                          title={`${EVIDENCE[s.evidence_status]?.label} · confidence ${s.confidence}`}
                          className="w-full py-2 rounded-sm font-mono font-medium transition-transform hover:scale-105"
                          style={{ background: RAYG(s.value), color: RAYG_TEXT(s.value) }}>
                          {s.value}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-4 mt-4 text-[11px] text-black/60">
          {[["#EF4444", "Red < 40"], ["#F59E0B", "Amber 40–59"], ["#FDE047", "Yellow 60–74"], ["#22C55E", "Green ≥ 75"]].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: c }} />{l}</span>
          ))}
          <span className="ml-4">Hover a cell for evidence status and confidence.</span>
        </div>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-sm p-6 w-96 fade-up" onClick={(e) => e.stopPropagation()} data-testid="score-edit-modal">
            <h3 className="font-display font-bold text-lg tracking-tight mb-1">Adjust score</h3>
            <p className="text-[12px] text-black/55 mb-4">{deptByKey[editing.dept]?.name} — {SCORE_DIMS.find(([k]) => k === editing.dim)?.[1]}. This change is audited and marked user-reported.</p>
            <input type="range" min="0" max="100" value={value} onChange={(e) => setValue(e.target.value)} data-testid="score-slider" className="w-full accent-[#D4AF37]" />
            <div className="text-center font-display text-4xl font-black my-2" style={{ color: RAYG(value) }}>{value}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-xs border border-black/20 rounded-sm px-3 py-2">Cancel</button>
              <button onClick={save} data-testid="score-save-btn" className="text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2 font-semibold">Save score</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
